/**
 * RecoveryKeyModal Component
 * Modal that prompts for recovery key after login when required
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { CryptoEvent } from '../../constants/matrixEvents';
import logger from '../../utils/logger';
import PropTypes from 'prop-types';
import RecoveryKeyInput, { normalizeRecoveryKey } from './RecoveryKeyInput';
// Note: Modal confirm/warn removed for immediate actions in tests
import { useRecovery, useRecoveryActions } from '../../context/RecoveryContext/index';
import { useI18n } from '../../context/I18nContext';
import { useModal } from '../../context/ModalContext';
import { useMatrixClient } from '../../context/MatrixContext';
import { isRecoveryCompleted } from '../../context/RecoveryContext/storage';
import { RecoveryStep } from '../../context/RecoveryContext/types';
import VerificationModal from '../verification/VerificationModal';
import { trustEventEmitter, TrustEvents } from '../../crypto/events';
import { fetchDisplayName } from '../../utils/profileUtils';
import {
  encryptRecoveryKey,
  decryptRecoveryKey,
  getStorageKey,
} from '../../utils/recoveryKeyStorage';
import './RecoveryKeyModal.css';

const RecoveryKeyModal = ({
  onSuccess,
  onSkip,
  onReset,
  validator,
  allowSkip = true,
  allowReset = true,
}) => {
  const [keyValue, setKeyValue] = useState('');
  const [formatValid, setFormatValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Removed confirm/warn UI for skip/reset to make actions immediate
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verified, setVerified] = useState(false);
  const [validatedKey, setValidatedKey] = useState(null);

  const inputRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  // Track if verification recently completed so we can handle gossiped keys
  const verificationCompletedRef = useRef(false);
  const backupRestoredRef = useRef(false);

  const { showConfirm } = useModal();
  const client = useMatrixClient();
  const recoveryState = useRecovery();
  const recoveryActions = useRecoveryActions();
  const { t } = useI18n();

  // Check if recovery has already been completed
  const hasCompletedRecovery = useMemo(() => {
    if (!client) return false;
    const deviceId = client.getDeviceId();
    if (!deviceId) {
      logger.debug('[RecoveryKeyModal] Device ID not available yet');
      return false;
    }
    return isRecoveryCompleted(deviceId) || recoveryState.step === RecoveryStep.DONE;
  }, [client, recoveryState.step]);

  // Show success indicator when recovery is completed or key was just validated
  const showSuccess = hasCompletedRecovery || validatedKey !== null;

  // Load validated key from sessionStorage when modal opens with completed recovery
  useEffect(() => {
    const loadEncryptedKey = async () => {
      if (hasCompletedRecovery && !validatedKey && client) {
        const userId = client.getUserId();
        if (userId) {
          const encryptedKey = sessionStorage.getItem(getStorageKey(userId));
          if (encryptedKey) {
            try {
              const decryptedKey = await decryptRecoveryKey(encryptedKey, userId);
              setValidatedKey(decryptedKey);
            } catch (err) {
              logger.warn('RecoveryKeyModal: Failed to decrypt stored key:', err.message);
              // Clear invalid encrypted data
              sessionStorage.removeItem(getStorageKey(userId));
            }
          }
        }
      }
    };
    loadEncryptedKey();
  }, [hasCompletedRecovery, validatedKey, client]);

  // Error code to translation key mapping
  const getErrorMessage = useCallback((errorCode) => {
    const errorKeys = {
      INVALID_FORMAT: 'RecoveryKeyModal.errorInvalidFormat',
      INVALID_RECOVERY_KEY: 'RecoveryKeyModal.errorInvalidKey',
      NETWORK_TIMEOUT: 'RecoveryKeyModal.errorNetworkTimeout',
      CORRUPTED_BACKUP: 'RecoveryKeyModal.errorCorruptedBackup',
      CRYPTO_NOT_READY: 'RecoveryKeyModal.errorCryptoNotReady',
      VERIFY_FAILED: 'RecoveryKeyModal.errorVerifyFailed',
      NO_BACKUP: 'RecoveryKeyModal.errorNoBackup',
      SSSS_VERSION_MISMATCH: 'RecoveryKeyModal.errorVersionMismatch',
      SSSS_WEAK_KDF_PARAMS: 'RecoveryKeyModal.errorWeakKdfParams',
      VERIFICATION_CONFLICT: 'RecoveryKeyModal.errorVerificationConflict',
      TOO_MANY_ATTEMPTS: 'RecoveryKeyModal.errorTooManyAttempts',
      PERSISTENCE_ERROR: 'RecoveryKeyModal.errorPersistence',
      UNKNOWN: 'RecoveryKeyModal.errorUnknown',
    };
    const key = errorKeys[errorCode] || errorKeys.UNKNOWN;
    return t(key);
  }, [t]);

  // Focus management
  useEffect(() => {
    // Focus input on mount
    if (inputRef.current) {
      inputRef.current.focus();
    }

    return () => {
      // Clear cooldown timer on unmount
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  // Handle cooldown countdown
  useEffect(() => {
    if (cooldownSeconds > 0) {
      cooldownTimerRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, [cooldownSeconds]);

  // Listen for KeyBackupDecryptionKeyCached to handle race condition
  // where backup key arrives via gossip AFTER verification completes
  useEffect(() => {
    if (!client) return;

    const handleBackupKeyCached = async (backupVersion) => {
      // Only act if verification recently completed and backup wasn't already restored
      if (!verificationCompletedRef.current || backupRestoredRef.current) {
        return;
      }

      logger.info(`RecoveryKeyModal: KeyBackupDecryptionKeyCached event received for version ${backupVersion}`);

      try {
        const crypto = client.getCrypto?.();
        if (!crypto) return;

        // Now that the key is cached, we can restore the backup
        if (typeof crypto.restoreKeyBackup === 'function') {
          logger.debug('RecoveryKeyModal: Restoring keys from backup after key was cached...');
          try {
            await crypto.restoreKeyBackup();
            logger.info('RecoveryKeyModal: Key backup restored successfully after gossip');
            backupRestoredRef.current = true;

            // Emit VERIFICATION_COMPLETE to trigger re-decryption in active rooms
            trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_COMPLETE, {
              verificationType: 'device',
              source: 'recovery-key-modal-gossip',
            });
          } catch (backupErr) {
            logger.debug('RecoveryKeyModal: Key backup restore failed after gossip:', backupErr.message);
          }
        }
      } catch (err) {
        logger.error('RecoveryKeyModal: Error restoring keys after gossip:', err);
      }
    };

    client.on(CryptoEvent.KeyBackupDecryptionKeyCached, handleBackupKeyCached);

    return () => {
      client.off(CryptoEvent.KeyBackupDecryptionKeyCached, handleBackupKeyCached);
    };
  }, [client]);

  /**
   * Handle key input change
   */
  const handleKeyChange = useCallback((value, meta) => {
    setKeyValue(value);
    setFormatValid(meta.isFormatValid);
    setError(null); // Clear error on new input
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    if (!formatValid || loading || cooldownSeconds > 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Normalize the key
      const { normalized } = normalizeRecoveryKey(keyValue);

      // Validate and submit through the validator
      const result = await validator.onSubmit(normalized);

      if (result.success) {
        // Clear input field
        setKeyValue('');

        // Store validated key encrypted in sessionStorage for persistence and export
        const userId = client?.getUserId();
        if (userId) {
          try {
            const encryptedKey = await encryptRecoveryKey(normalized, userId);
            sessionStorage.setItem(getStorageKey(userId), encryptedKey);
          } catch (encryptErr) {
            logger.warn('RecoveryKeyModal: Failed to encrypt key for storage:', encryptErr.message);
            // Continue without storing - key will still be available in memory for this session
          }
        }
        setValidatedKey(normalized);

        // Call success callback
        if (onSuccess) {
          await onSuccess(result);
        }

        // Update recovery context
        if (recoveryActions?.submitKey) {
          await recoveryActions.submitKey(normalized);
        }
      } else {
        // Handle validation error
        const errorCode = result.error || 'UNKNOWN';
        const errorMessage = getErrorMessage(errorCode);

        setError(errorMessage);

        // Handle rate limiting
        if (errorCode === 'TOO_MANY_ATTEMPTS') {
          const seconds = result.cooldownSeconds ?? (result.cooldownMs ? Math.ceil(result.cooldownMs / 1000) : 0);
          if (seconds > 0) setCooldownSeconds(seconds);
        }

        // Update remaining attempts if provided
        if (result.remainingAttempts !== undefined) {
          setRemainingAttempts(result.remainingAttempts);
        }

        // Clear sensitive data on certain errors
        if (['CORRUPTED_BACKUP', 'SSSS_VERSION_MISMATCH'].includes(errorCode)) {
          setKeyValue('');
          setFormatValid(false);
        }
      }
    } catch (err) {
      logger.error('RecoveryKeyModal: Submission error', err);
      setError(getErrorMessage('UNKNOWN'));
    } finally {
      setLoading(false);
    }
  }, [keyValue, formatValid, loading, cooldownSeconds, validator, onSuccess, recoveryActions, getErrorMessage, client]);

  /**
   * Handle Skip action
   */
  const handleSkip = useCallback(() => {
    if (!allowSkip) return;

    // Trigger skip immediately without confirm modal
    if (onSkip) {
      onSkip();
    }
    if (recoveryActions?.skip) {
      recoveryActions.skip();
    }
  }, [allowSkip, onSkip, recoveryActions]);

  /**
   * Handle Reset backup action
   */
  const handleReset = useCallback(async () => {
    if (!allowReset) return;

    // Trigger reset immediately without confirm modal
    setLoading(true);
    try {
      if (onReset) {
        await onReset();
      }
      if (recoveryActions?.reset) {
        await recoveryActions.reset();
      }

      // Only clear stored recovery key AFTER successful reset
      const userId = client?.getUserId();
      if (userId) {
        sessionStorage.removeItem(getStorageKey(userId));
      }
      setValidatedKey(null);
    } catch (err) {
      logger.error('RecoveryKeyModal: Reset backup error', err);
      setError(t('RecoveryKeyModal.errorResetFailed'));
    } finally {
      setLoading(false);
    }
  }, [allowReset, onReset, recoveryActions, t, client]);

  /**
   * Perform the actual export of recovery key as JSON file
   */
  const performExport = useCallback(async () => {
    if (!validatedKey) return;

    const userId = client?.getUserId();
    const displayName = await fetchDisplayName(client, userId);

    const exportData = {
      mxid: userId || '',
      displayName,
      recoveryKey: validatedKey,
      exportedAt: new Date().toISOString(),
    };

    let url;
    try {
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'recovery-key.json';
      // snyk:ignore:DOM-based Cross-site Scripting (XSS) -- URL is generated from trusted Blob and filename is static
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      if (url) {
        URL.revokeObjectURL(url);
      }
    }
  }, [validatedKey, client]);

  /**
   * Handle Export Key action - shows warning then exports recovery key as JSON file
   */
  const handleExportKey = useCallback(() => {
    if (!validatedKey) return;

    showConfirm(
      t('RecoveryKeyModal.exportWarningTitle'),
      t('RecoveryKeyModal.exportWarningMessage'),
      performExport,
    );
  }, [validatedKey, showConfirm, t, performExport]);

  /**
   * Handle Recovery Key Lost link click
   * Shows a warning confirmation, then proceeds with reset if user acknowledges
   */
  const handleRecoveryKeyLost = useCallback(() => {
    showConfirm(
      t('RecoveryKeyModal.keyLostWarningTitle'),
      t('RecoveryKeyModal.keyLostWarningMessage'),
      handleReset,
    );
  }, [showConfirm, t, handleReset]);

  /**
   * Handle Transfer from other device button click
   */
  const handleTransferFromDevice = useCallback(() => {
    setShowVerificationModal(true);
    // Note: Don't close recovery modal yet - let verification complete first
  }, []);

  /**
   * Handle device verification completion
   * After successful SAS verification, restore keys from backup to enable message decryption
   */
  const handleVerificationComplete = useCallback(async (success) => {
    logger.info(`RecoveryKeyModal: Device verification completed with success=${success}`);
    setShowVerificationModal(false);

    if (success) {
      // Mark as verified to keep modal hidden during cleanup
      setVerified(true);

      // Mark that verification completed - used by KeyBackupDecryptionKeyCached handler
      verificationCompletedRef.current = true;

      // Restore keys from backup after successful verification
      // This enables decryption of historical messages without page reload
      // Reference: Element Web's SetupEncryptionStore.ts usePassPhrase() approach
      if (client) {
        try {
          const crypto = client.getCrypto?.();
          if (crypto) {
            // Step 1: Load the backup private key from secret storage (4S)
            if (typeof crypto.loadSessionBackupPrivateKeyFromSecretStorage === 'function') {
              logger.debug('RecoveryKeyModal: Loading backup key from secret storage...');
              try {
                await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
                logger.info('RecoveryKeyModal: Backup key loaded from secret storage');
              } catch (loadErr) {
                // May fail if key was gossiped differently - KeyBackupDecryptionKeyCached will handle
                logger.debug('RecoveryKeyModal: Failed to load backup key from 4S (will retry when key arrives):', loadErr.message);
              }
            }

            // Step 2: Restore the key backup (download all megolm keys)
            if (typeof crypto.restoreKeyBackup === 'function') {
              logger.debug('RecoveryKeyModal: Restoring keys from backup...');
              try {
                await crypto.restoreKeyBackup();
                logger.info('RecoveryKeyModal: Key backup restored successfully');
                backupRestoredRef.current = true;
              } catch (backupErr) {
                // May fail if key not available yet - KeyBackupDecryptionKeyCached will handle
                logger.debug('RecoveryKeyModal: Key backup restore failed (will retry when key arrives):', backupErr.message);
              }
            }

            // Step 3: Emit VERIFICATION_COMPLETE so any active rooms can re-decrypt messages
            logger.debug('RecoveryKeyModal: Emitting VERIFICATION_COMPLETE to trigger re-decryption');
            trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_COMPLETE, {
              verificationType: 'device',
              source: 'recovery-key-modal',
            });
          }
        } catch (err) {
          logger.error('RecoveryKeyModal: Error restoring keys after verification:', err);
        }
      }

      // Verification succeeded - notify parent who will handle completion
      if (onSuccess) {
        onSuccess({ verified: true });
      }
    } else {
      // Verification failed or cancelled - user can retry or skip
      if (onSkip) {
        onSkip();
      }
      if (recoveryActions?.skip) {
        recoveryActions.skip();
      }
    }
  }, [client, onSuccess, onSkip, recoveryActions]);

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback((e) => {
    // Enter key submits if valid
    if (e.key === 'Enter' && formatValid && !loading && cooldownSeconds === 0) {
      handleSubmit(e);
    }

    // Escape key opens skip dialog if allowed
    if (e.key === 'Escape' && allowSkip && !loading) {
      e.preventDefault();
      handleSkip();
    }
  }, [formatValid, loading, cooldownSeconds, allowSkip, handleSubmit, handleSkip]);

  const isSubmitDisabled = !formatValid || loading || cooldownSeconds > 0;

  return (
    <>
      <div
        className="recovery-key-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recovery-modal-title"
        aria-describedby="recovery-modal-description"
        data-testid="recovery-key-modal"
        style={{ display: (showVerificationModal || verified) ? 'none' : 'flex' }}
      >
      <div className="recovery-key-modal-container">
        <div className="recovery-key-modal-header">
          <h2 id="recovery-modal-title" className="recovery-key-modal-title">
            {t('RecoveryKeyModal.title')}
          </h2>
          {cooldownSeconds === 0 && (
            <button
              className="recovery-key-modal-close"
              onClick={handleSkip}
              aria-label="Close"
              data-testid="close-btn"
            >
              ×
            </button>
          )}
        </div>

        <div className="recovery-key-modal-content">
          <div id="recovery-modal-description" className="recovery-key-modal-description">
            <p>
              {t('RecoveryKeyModal.description')}
            </p>
            <div className="recovery-key-example">
              <span className="recovery-key-example-label">{t('RecoveryKeyModal.exampleLabel')}</span>
              <code className="recovery-key-example-code">
                {t('RecoveryKeyModal.exampleCode')}
              </code>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="recovery-key-form">
            {showSuccess ? (
              <div className="recovery-key-success" data-testid="recovery-key-success" role="status">
                <div className="recovery-key-success-icon">
                  <ion-icon name="checkmark" />
                </div>
                <span className="recovery-key-success-message">
                  {t('RecoveryKeyModal.successMessage')}
                </span>
              </div>
            ) : (
              <>
                <RecoveryKeyInput
                  value={keyValue}
                  onChange={handleKeyChange}
                  disabled={loading || hasCompletedRecovery}
                  autoFocus
                  inputRef={inputRef}
                  onKeyDown={handleKeyDown}
                  error={!!error}
                  placeholder={t('RecoveryKeyModal.inputPlaceholder')}
                  aria-describedby={error
                    ? 'recovery-modal-description recovery-error-message'
                    : 'recovery-modal-description'
                  }
                />

                {error && (
                  <div
                    id="recovery-error-message"
                    className="recovery-key-error"
                    role="alert"
                    aria-live="polite"
                    data-testid="error-message"
                  >
                    <ion-icon name="alert-circle" />
                    <span>{error}</span>
                  </div>
                )}

                {remainingAttempts !== null && remainingAttempts < 3 && (
                  <div className="recovery-key-warning" role="status">
                    {t('RecoveryKeyModal.warningRemainingAttempts', { count: remainingAttempts })}
                  </div>
                )}

                {cooldownSeconds > 0 && (
                  <div className="recovery-key-cooldown" role="status">
                    {t('RecoveryKeyModal.cooldownMessage', { seconds: cooldownSeconds })}
                  </div>
                )}
              </>
            )}

            {showSuccess ? (
              // Success state: Export Key and Reset Recovery Key buttons
              <>
                <div className="recovery-key-modal-actions recovery-key-modal-actions--success">
                  <button
                    type="button"
                    className="recovery-key-btn recovery-key-btn-primary"
                    onClick={handleExportKey}
                    disabled={!validatedKey}
                    data-testid="export-key-btn"
                  >
                    {t('RecoveryKeyModal.exportKeyButton')}
                  </button>

                  <button
                    type="button"
                    className="recovery-key-btn recovery-key-btn-secondary"
                    onClick={handleReset}
                    disabled={loading}
                    data-testid="reset-key-btn"
                  >
                    {t('RecoveryKeyModal.resetKeyButton')}
                  </button>
                </div>

                {!validatedKey && (
                  <div className="recovery-key-export-note" data-testid="export-note">
                    <ion-icon name="information-circle-outline" />
                    <span>{t('RecoveryKeyModal.exportKeyNote')}</span>
                  </div>
                )}
              </>
            ) : (
              // Input state: existing buttons (Skip, Continue, Transfer, etc.)
              <>
                <div className="recovery-key-modal-actions">
                  {allowSkip && (
                    <button
                      type="button"
                      className="recovery-key-btn recovery-key-btn-secondary"
                      onClick={handleSkip}
                      disabled={loading}
                      data-testid="skip-btn"
                    >
                      {t('RecoveryKeyModal.skipButton')}
                    </button>
                  )}

                  <button
                    type="submit"
                    className="recovery-key-btn recovery-key-btn-primary"
                    disabled={isSubmitDisabled}
                    data-testid="continue-btn"
                    aria-busy={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="recovery-key-spinner"
                          data-testid="loading-spinner"
                          aria-hidden="true"
                        />
                        {t('RecoveryKeyModal.verifying')}
                      </>
                    ) : cooldownSeconds > 0 ? (
                      t('RecoveryKeyModal.waitSeconds', { seconds: cooldownSeconds })
                    ) : (
                      t('RecoveryKeyModal.continueButton')
                    )}
                  </button>
                </div>

                <div className="recovery-key-separator">{t('RecoveryKeyModal.separator')}</div>

                <button
                  type="button"
                  className="recovery-key-btn recovery-key-btn-alternative"
                  onClick={handleTransferFromDevice}
                  disabled={loading || hasCompletedRecovery}
                  data-testid="transfer-device-btn"
                >
                  {t('RecoveryKeyModal.transferButton')}
                </button>

                <div className="recovery-key-modal-footer">
                  <button
                    type="button"
                    onClick={handleRecoveryKeyLost}
                    className="recovery-key-footer-link"
                    data-testid="help-link"
                  >
                    {t('RecoveryKeyModal.footerLink')}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      </div>

      {showVerificationModal && (
        <VerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onComplete={handleVerificationComplete}
        />
      )}
    </>
  );
};

RecoveryKeyModal.propTypes = {
  onSuccess: PropTypes.func,
  onSkip: PropTypes.func,
  onReset: PropTypes.func,
  validator: PropTypes.shape({
    onSubmit: PropTypes.func.isRequired,
  }).isRequired,
  allowSkip: PropTypes.bool,
  allowReset: PropTypes.bool,
};

export default RecoveryKeyModal;
